import {
  OrderAction,
  OrderEvent,
  OrderPaymentEvent,
  OrderPaymentProcessedEvent,
} from '@oolio-group/domain';
import { sortKey } from '@oolio-group/order-helper';

/**
 * True when an ORDER_OPEN in `events` happened after the matching
 * ORDER_PAYMENT — the attempt was abandoned. Fails open if no match. OC-6515.
 */
export function isPaymentAttemptSuperseded(
  events: OrderEvent[],
  processedEvent: OrderPaymentProcessedEvent,
): boolean {
  if (!processedEvent.paymentRequestId) return false;

  const paymentEvent = events.find(
    (event): event is OrderPaymentEvent =>
      event.action === OrderAction.ORDER_PAYMENT &&
      (event as OrderPaymentEvent).paymentRequestId ===
        processedEvent.paymentRequestId,
  );
  if (!paymentEvent) return false;

  return events.some(
    event =>
      event.action === OrderAction.ORDER_OPEN && isAfter(event, paymentEvent),
  );
}

// Shared hlc-aware sortKey; a per-pair hlc-or-timestamp fallback isn't transitive.
function isAfter(event: OrderEvent, other: OrderEvent): boolean {
  return sortKey(event) > sortKey(other);
}

/**
 * True when `events` has a still-current ORDER_OPEN reopen — no later
 * ORDER_PAYMENT exists. See OC-6515.
 */
export function isReopenStillActive(events: OrderEvent[]): boolean {
  const reopenEvent = events
    .filter(event => event.action === OrderAction.ORDER_OPEN)
    .reduce<OrderEvent | undefined>(
      (latest, event) => (!latest || isAfter(event, latest) ? event : latest),
      undefined,
    );
  if (!reopenEvent) return false;

  return !events.some(
    event =>
      event.action === OrderAction.ORDER_PAYMENT && isAfter(event, reopenEvent),
  );
}
